import {
  adoptFeatures,
  describe,
  downloadGeoJSON,
  featureCollection,
  fillStyle,
  pointFeature,
  polygonFeature,
} from '../../common/geojson';
import { reprojectPoint } from '../shoreS/reprojection';
import { DEPTH_POINT_LAYER } from './depthPoints';

/**
 * @constant GEOJSON_MODULE
 * @description Module key, used to name the downloaded file.
 * @memberof module:Bathymetry
 */
const GEOJSON_MODULE = 'bathymetry';

/**
 * @constant WGS84
 * @description The CRS GeoJSON is defined in. Anything written here has to arrive in it.
 * @memberof module:Bathymetry
 */
const WGS84 = 'EPSG:4326';

/**
 * @function trainingFeatures
 * @description The surveyed depth points the model was calibrated against, one feature each.
 * These are uploaded in whatever projection the survey came in - a UTM zone, typically - while
 * GeoJSON is WGS84 by definition, so anything else is reprojected on the way out. A point that
 * cannot be reprojected is left out rather than written at the wrong place on the map.
 * @param {Array<Array<Number>>} [matrix] - `[x, y, depth]` rows.
 * @param {String} [coordinatesSystem] - The CRS those rows are in, e.g. `'EPSG:32722'`.
 * @memberof module:Bathymetry
 * @returns {Array<Object|null>} The features.
 */
const trainingFeatures = (matrix, coordinatesSystem) => {
  if (!matrix?.length) return [];

  const needsReprojection = Boolean(coordinatesSystem) && coordinatesSystem.indexOf('4326') === -1;

  return matrix.map(([x, y, depth]) => {
    let coordinate = [x, y];
    if (needsReprojection) {
      try {
        coordinate = reprojectPoint([Number(x), Number(y)], coordinatesSystem, WGS84);
      } catch (error) {
        console.error('[Bathymetry] could not reproject a training point', error);
        return null;
      }
    }
    return pointFeature(coordinate, 'training_points', {
      observed_depth: depth,
      ...describe(`Depth ${depth} m`, 'Surveyed depth used to calibrate the model'),
    });
  });
};

/**
 * @constant DEPTH_POINT_COLORS
 * @description Marker colours by depth band, shallow first, sampled from the ramp the result page
 * draws the depth map with so the file opens looking like what the app showed. A point deeper than
 * the last colour reuses it.
 * @memberof module:Bathymetry
 */
const DEPTH_POINT_COLORS = [
  '#00ffff',
  '#00d7ec',
  '#00bcdf',
  '#00a0d2',
  '#0084c5',
  '#0069b8',
  '#004dab',
  '#00329e',
  '#001691',
  '#000a84',
  '#000577',
  '#00006a',
];

/**
 * @function depthPointFeatures
 * @description The modelled depth as one point per pixel, ready to be written.
 * The points arrive already evaluated from Earth Engine - see {@link depthPointCollection} - so
 * the geometry is taken as it came and only named, described and coloured here. Each carries the
 * depth at full precision and the 2 m band it falls in, so a GIS can interpolate from the value or
 * colour by the class without recomputing either.
 * This replaced an export of depth bands as polygons. Those were cut along a quantised value, so
 * their borders followed the pixel grid rather than the sea bed and the layer read as a jigsaw
 * whose pieces did not meet. Points have no borders to disagree about.
 * @param {Object} [points] - An evaluated `ee.FeatureCollection` of depth points.
 * @memberof module:Bathymetry
 * @returns {Array<Object>} The features.
 */
const depthPointFeatures = (points) => {
  if (!points?.features?.length) return [];

  return points.features.flatMap((point) => {
    const { depth_m: depth, depth_min_m: band } = point.properties || {};
    const value = Number(depth);
    const floor = Number.isFinite(Number(band)) ? Number(band) : Math.floor(value / 2) * 2;
    const color = DEPTH_POINT_COLORS[Math.min(DEPTH_POINT_COLORS.length - 1, Math.max(0, Math.floor(floor / 2)))];
    return adoptFeatures(point, DEPTH_POINT_LAYER, {
      depth_m: Number.isFinite(value) ? Number(value.toFixed(3)) : null,
      depth_min_m: floor,
      depth_max_m: floor + 2,
      ...describe(
        Number.isFinite(value) ? `Depth ${value.toFixed(2)} m` : 'Depth',
        `Modelled depth, in the ${floor}-${floor + 2} m band`,
      ),
      'marker-color': color,
    });
  });
};

/**
 * @function buildGeoJsonExport
 * @description Gathers what this analysis holds as vectors: the area of interest, the surveyed
 * depth points the model was calibrated against, and the modelled depth itself as one point per
 * pixel.
 * The depth surface is a raster, and the result page offers it as GeoTIFF for the cases that want
 * one. It is vectorised here as well because a GeoTIFF cannot be selected, measured or joined to a
 * table, which is most of what the people receiving these files do with them. The two are not
 * alternatives: the raster keeps every pixel, the vectors keep what can be worked with.
 * @param {Object} params
 * @param {Object} [params.fileData] - The uploaded inputs, holding `coordinates`, `trainingMatrix`
 *  and `coordinatesSystem`.
 * @param {Object} [params.depthPoints] - Depth points already evaluated from Earth Engine.
 *  Omitted, the file is written without them rather than not written at all.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page, through the GIS export icon.
 * @memberof module:Bathymetry
 * @returns {Object} A FeatureCollection, empty when nothing has been uploaded yet.
 */
export const buildGeoJsonExport = ({ fileData, depthPoints }) =>
  featureCollection([
    polygonFeature(fileData?.coordinates, 'area_of_interest', {
      ...describe('Area of interest'),
      ...fillStyle('#42a5f5', 0),
    }),
    depthPointFeatures(depthPoints),
    trainingFeatures(fileData?.trainingMatrix, fileData?.coordinatesSystem),
  ]);

/**
 * @function exportGeoJson
 * @description Builds the collection and hands it to the browser.
 * @param {Object} params - Same as {@link buildGeoJsonExport}.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page.
 * @memberof module:Bathymetry
 * @returns {Boolean} False when there was nothing to write.
 */
export const exportGeoJson = (params) => downloadGeoJSON(buildGeoJsonExport(params), GEOJSON_MODULE);
